import React, { useState, useCallback, useMemo } from 'react';
import Select from 'react-select';
import { Tags, Briefcase, Phone, Mail, MailOpen, MapPin, Heading, Search, DollarSign, ChevronUp, ChevronDown, X, Briefcase as Experience, UserCheck, Building2 } from 'lucide-react';

// Composant TagFilter pour le panneau
export const TagFilter = React.memo(({ onFilter, currentTags = [] }) => {
  const [filterTags, setFilterTags] = useState(currentTags);
  const [newTag, setNewTag] = useState("");

  // Sync with parent state when currentTags changes
  React.useEffect(() => {
    setFilterTags(currentTags);
  }, [currentTags]);

  const addTag = useCallback((e) => {
    if (e.key === "Enter" && newTag.trim() !== "") {
      const lowerCaseTag = newTag.trim().toLowerCase();
      if (!filterTags.includes(lowerCaseTag)) {
        const updatedTags = [...filterTags, lowerCaseTag];
        setFilterTags(updatedTags);
        onFilter(updatedTags);
      }
      setNewTag("");
    }
  }, [filterTags, newTag, onFilter]);

  const removeTag = useCallback((tagToRemove) => {
    const updatedTags = filterTags.filter((tag) => tag !== tagToRemove);
    setFilterTags(updatedTags);
    onFilter(updatedTags);
  }, [filterTags, onFilter]);

  return (
    <div className="space-y-3">
      <div>
        <input
          type="text"
          value={newTag}
          onChange={(e) => setNewTag(e.target.value)}
          onKeyDown={addTag}
          placeholder="Filter par tag..."
          className="w-full text-sm px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
        />
      </div>
      
      {filterTags.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {filterTags.map((tag, index) => (
            <div
              key={index}
              className="flex items-center bg-indigo-100 text-indigo-800 px-3 py-1 rounded-full text-sm"
            >
              <span>{tag}</span>
              <button
                onClick={() => removeTag(tag)}
                className="ml-2 text-indigo-600 hover:text-indigo-800 transition-colors"
              >
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
});

// Nouvelle sidebar de filtres (toujours visible)
export const FilterSidebar = React.memo(({
  onTagFilter,
  positions,
  onPositionFilter,
  showOnlyWithPhone,
  onPhoneFilter,
  showOnlyWithProfessionalEmail,
  onProfessionalEmailToggle,
  showOnlyWithPersonalEmail,
  onPersonalEmailToggle,
  currentTags,
  selectedPositions,
  cityFilter,
  onCityFilter,
  personalEmailFilter,
  onPersonalEmailFilter,
  professionalEmailFilter,
  onProfessionalEmailFilter,
  jobTitleFilter,
  onJobTitleFilter,
  keywordFilter,
  onKeywordFilter,
  yearsOfExperienceFilter,
  onYearsOfExperienceFilter,
  salaryFilters,
  onSalaryFiltersChange,
  openToJobOpportunities,
  onOpenToJobOpportunitiesToggle,
  bizdevProcesses,
  selectedBizdevProcesses,
  onBizdevProcessFilter,
  isMobile = false
}) => {
  // État pour gérer les sections ouvertes/fermées
  const [openSections, setOpenSections] = useState({
    tags: false,
    positions: false,
    phone: false,
    professionalEmail: false,
    personalEmail: false,
    city: false,
    jobTitle: false,
    keyword: false,
    yearsOfExperience: false,
    emailSearch: false,
    currentSalary: false,
    expectedSalary: false,
    openToJobOpportunities: false,
    bizdevProcesses: false,
  });

  const toggleSection = useCallback((section) => {
    setOpenSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  }, []);

  const positionOptions = useMemo(() =>
    positions.map(position => ({
      value: position._id,
      label: position.title
    })), [positions]
  );

  const selectedPositionOptions = useMemo(() =>
    positionOptions.filter(option => selectedPositions.includes(option.value)),
    [positionOptions, selectedPositions]
  );

  const handlePositionFilterChange = useCallback((selected) => {
    const positionIds = selected ? selected.map(option => option.value) : [];
    onPositionFilter(positionIds);
  }, [onPositionFilter]);

  const bizdevProcessOptions = useMemo(() =>
    (bizdevProcesses || []).map(process => ({
      value: process._id,
      label: process.name
    })), [bizdevProcesses]
  );

  const selectedBizdevProcessOptions = useMemo(() =>
    bizdevProcessOptions.filter(option => selectedBizdevProcesses.includes(option.value)),
    [bizdevProcessOptions, selectedBizdevProcesses]
  );

  const handleBizdevProcessFilterChange = useCallback((selected) => {
    const processIds = selected ? selected.map(option => option.value) : [];
    onBizdevProcessFilter(processIds);
  }, [onBizdevProcessFilter]);

  const customSelectStyles = {
    control: (provided, state) => ({
      ...provided,
      borderColor: '#D1D5DB',
      borderRadius: '6px',
      minHeight: '38px',
      fontSize: '14px',
      boxShadow: state.isFocused ? '0 0 0 2px rgba(79, 70, 229, 0.2)' : 'none',
      '&:hover': {
        borderColor: '#9CA3AF'
      }
    }),
    menu: (provided) => ({
      ...provided,
      borderRadius: '6px',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
      fontSize: '14px',
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? '#4F46E5' : state.isFocused ? '#EEF2FF' : 'white',
      color: state.isSelected ? 'white' : '#1F2937',
      fontSize: '14px',
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: '#EEF2FF',
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: '#4F46E5',
      fontSize: '12px',
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: '#4F46E5',
      '&:hover': {
        backgroundColor: '#E0E7FF',
        color: '#4338CA',
      },
    }),
  };

  // Rendu conditionnel selon le mode mobile ou desktop
  const content = (
    <>
      <div className="space-y-2 max-h-[calc(100vh-200px)] overflow-y-auto minimal-scrollbar">
        {/* Filter by Tags */}
        <div className="border-b border-gray-200 px-4">
          <button
            onClick={() => toggleSection('tags')}
            className="w-full flex items-center justify-between py-3 text-sm font-medium text-gray-700 hover:text-gray-900"
          >
            <span className="flex items-center gap-2">
              <Tags size={16} />
              Tags
            </span>
            {openSections.tags ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
          {openSections.tags && (
            <div className="pb-3">
              <TagFilter onFilter={onTagFilter} currentTags={currentTags} />
            </div>
          )}
        </div>

        {/* Filter by Positions */}
        <div className="border-b border-gray-200 px-4">
          <button
            onClick={() => toggleSection('positions')}
            className="w-full flex items-center justify-between py-3 text-sm font-medium text-gray-700 hover:text-gray-900"
          >
            <span className="flex items-center gap-2">
              <Briefcase size={16} />
                Recrutement
            </span>
            {openSections.positions ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
          {openSections.positions && (
            <div className="pb-3">
              <Select
                isMulti
                name="positions"
                options={positionOptions}
                value={selectedPositionOptions}
                placeholder="Sélectionner..."
                onChange={handlePositionFilterChange}
                styles={customSelectStyles}
                noOptionsMessage={() => "Aucun poste trouvé"}
              />
            </div>
          )}
        </div>

        {/* Filter by BizDev Process */}
        <div className="border-b border-gray-200 px-4">
          <button
            onClick={() => toggleSection('bizdevProcesses')}
            className="w-full flex items-center justify-between py-3 text-sm font-medium text-gray-700 hover:text-gray-900"
          >
            <span className="flex items-center gap-2">
              <Building2 size={16} />
              Prospection
            </span>
            {openSections.bizdevProcesses ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
          {openSections.bizdevProcesses && (
            <div className="pb-3">
              <Select
                isMulti
                name="bizdevProcesses"
                options={bizdevProcessOptions}
                value={selectedBizdevProcessOptions}
                placeholder="Sélectionner..."
                onChange={handleBizdevProcessFilterChange}
                styles={customSelectStyles}
                noOptionsMessage={() => "Aucun process trouvé"}
              />
            </div>
          )}
        </div>

        {/* Filter by Open to Job Opportunities */}
        <div className="border-b border-gray-200 px-4">
          <button
            onClick={() => toggleSection('openToJobOpportunities')}
            className="w-full flex items-center justify-between py-3 text-sm font-medium text-gray-700 hover:text-gray-900"
          >
            <span className="flex items-center gap-2">
              <UserCheck size={16} />
              Ouvert aux opportunités
            </span>
            {openSections.openToJobOpportunities ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
          {openSections.openToJobOpportunities && (
            <div className="pb-3">
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="sr-only"
                  checked={openToJobOpportunities}
                  onChange={() => onOpenToJobOpportunitiesToggle(!openToJobOpportunities)}
                />
                <div className={`w-11 h-6 rounded-full transition-colors ${openToJobOpportunities ? 'bg-indigo-600' : 'bg-gray-300'}`}>
                  <div className={`w-5 h-5 bg-white rounded-full transition-transform duration-200 transform ${openToJobOpportunities ? 'translate-x-5' : 'translate-x-0.5'} mt-0.5`}>
                  </div>
                </div>
                <span className="ml-3 text-sm text-gray-700">
                  Uniquement les contacts ouverts
                </span>
              </label>
            </div>
          )}
        </div>

        {/* Phone Filter */}
        <div className="border-b border-gray-200 px-4">
          <button
            onClick={() => toggleSection('phone')}
            className="w-full flex items-center justify-between py-3 text-sm font-medium text-gray-700 hover:text-gray-900"
          >
            <span className="flex items-center gap-2">
              <Phone size={16} />
              Téléphone
            </span>
            {openSections.phone ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
          {openSections.phone && (
            <div className="pb-3">
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="sr-only"
                  checked={showOnlyWithPhone}
                  onChange={() => onPhoneFilter(!showOnlyWithPhone)}
                />
                <div className={`w-11 h-6 rounded-full transition-colors ${showOnlyWithPhone ? 'bg-indigo-600' : 'bg-gray-300'}`}>
                  <div className={`w-5 h-5 bg-white rounded-full transition-transform duration-200 transform ${showOnlyWithPhone ? 'translate-x-5' : 'translate-x-0.5'} mt-0.5`}>
                  </div>
                </div>
                <span className="ml-3 text-sm text-gray-700">
                  Avec téléphone
                </span>
              </label>
            </div>
          )}
        </div>

        {/* Professional Email Filter */}
        <div className="border-b border-gray-200 px-4">
          <button
            onClick={() => toggleSection('professionalEmail')}
            className="w-full flex items-center justify-between py-3 text-sm font-medium text-gray-700 hover:text-gray-900"
          >
            <span className="flex items-center gap-2">
              <MailOpen size={16} />
              Email pro
            </span>
            {openSections.professionalEmail ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
          {openSections.professionalEmail && (
            <div className="pb-3">
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="sr-only"
                  checked={showOnlyWithProfessionalEmail}
                  onChange={() => onProfessionalEmailToggle(!showOnlyWithProfessionalEmail)}
                />
                <div className={`w-11 h-6 rounded-full transition-colors ${showOnlyWithProfessionalEmail ? 'bg-indigo-600' : 'bg-gray-300'}`}>
                  <div className={`w-5 h-5 bg-white rounded-full transition-transform duration-200 transform ${showOnlyWithProfessionalEmail ? 'translate-x-5' : 'translate-x-0.5'} mt-0.5`}>
                  </div>
                </div>
                <span className="ml-3 text-sm text-gray-700">
                  Avec email pro
                </span>
              </label>
            </div>
          )}
        </div>

        {/* Personal Email Filter */}
        <div className="border-b border-gray-200 px-4">
          <button
            onClick={() => toggleSection('personalEmail')}
            className="w-full flex items-center justify-between py-3 text-sm font-medium text-gray-700 hover:text-gray-900"
          >
            <span className="flex items-center gap-2">
              <Mail size={16} />
              Email perso
            </span>
            {openSections.personalEmail ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
          {openSections.personalEmail && (
            <div className="pb-3">
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="sr-only"
                  checked={showOnlyWithPersonalEmail}
                  onChange={() => onPersonalEmailToggle(!showOnlyWithPersonalEmail)}
                />
                <div className={`w-11 h-6 rounded-full transition-colors ${showOnlyWithPersonalEmail ? 'bg-indigo-600' : 'bg-gray-300'}`}>
                  <div className={`w-5 h-5 bg-white rounded-full transition-transform duration-200 transform ${showOnlyWithPersonalEmail ? 'translate-x-5' : 'translate-x-0.5'} mt-0.5`}>
                  </div>
                </div>
                <span className="ml-3 text-sm text-gray-700">
                  Avec email perso
                </span>
              </label>
            </div>
          )}
        </div>

        {/* Filter by City */}
        <div className="border-b border-gray-200 px-4">
          <button
            onClick={() => toggleSection('city')}
            className="w-full flex items-center justify-between py-3 text-sm font-medium text-gray-700 hover:text-gray-900"
          >
            <span className="flex items-center gap-2">
              <MapPin size={16} />
              Localisation
            </span>
            {openSections.city ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
          {openSections.city && (
            <div className="pb-3">
              <input
                type="text"
                value={cityFilter}
                onChange={(e) => onCityFilter(e.target.value)}
                placeholder="Filtrer par ville..."
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
          )}
        </div>

        {/* Filter by Job Title */}
        <div className="border-b border-gray-200 px-4">
          <button
            onClick={() => toggleSection('jobTitle')}
            className="w-full flex items-center justify-between py-3 text-sm font-medium text-gray-700 hover:text-gray-900"
          >
            <span className="flex items-center gap-2">
              <Heading size={16} />
              Poste
            </span>
            {openSections.jobTitle ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
          {openSections.jobTitle && (
            <div className="pb-3">
              <input
                type="text"
                value={jobTitleFilter}
                onChange={(e) => onJobTitleFilter(e.target.value)}
                placeholder="Filtrer par poste..."
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
          )}
        </div>

        {/* Filter by Keyword */}
        <div className="border-b border-gray-200 px-4">
          <button
            onClick={() => toggleSection('keyword')}
            className="w-full flex items-center justify-between py-3 text-sm font-medium text-gray-700 hover:text-gray-900"
          >
            <span className="flex items-center gap-2">
              <Search size={16} />
              Mot-clé
            </span>
            {openSections.keyword ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
          {openSections.keyword && (
            <div className="pb-3 space-y-2">
              <input
                type="text"
                value={keywordFilter}
                onChange={(e) => onKeywordFilter(e.target.value)}
                placeholder="ex: react senior paris..."
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
              <div className="text-xs text-gray-500 space-y-1">
                <p>
                  <strong>Plusieurs mots-clés :</strong> Séparez-les par des espaces
                </p>
                <p className="text-gray-400">
                  Recherche dans : description, poste, tags, formation, expériences, éducation et notes
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Filter by Years of Experience */}
        <div className="border-b border-gray-200 px-4">
          <button
            onClick={() => toggleSection('yearsOfExperience')}
            className="w-full flex items-center justify-between py-3 text-sm font-medium text-gray-700 hover:text-gray-900"
          >
            <span className="flex items-center gap-2">
              <Experience size={16} />
              Années d'expérience
            </span>
            {openSections.yearsOfExperience ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
          {openSections.yearsOfExperience && (
            <div className="pb-3 space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Minimum :</span>
                <span className="font-semibold text-indigo-600">
                  {yearsOfExperienceFilter === 0
                    ? 'Tous'
                    : `${yearsOfExperienceFilter} an${yearsOfExperienceFilter > 1 ? 's' : ''}`}
                </span>
              </div>
              <input
                type="range"
                min="0"
                max="30"
                step="1"
                value={yearsOfExperienceFilter}
                onChange={(e) => onYearsOfExperienceFilter(Number(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-indigo-600"
                style={{
                  background: `linear-gradient(to right, #4F46E5 0%, #4F46E5 ${(yearsOfExperienceFilter / 30) * 100}%, #E5E7EB ${(yearsOfExperienceFilter / 30) * 100}%, #E5E7EB 100%)`
                }}
              />
              <div className="flex justify-between text-xs text-gray-500">
                <span>0 an</span>
                <span>15 ans</span>
                <span>30+ ans</span>
              </div>
              {yearsOfExperienceFilter > 0 && (
                <button
                  onClick={() => onYearsOfExperienceFilter(0)}
                  className="w-full text-xs text-indigo-600 hover:text-indigo-700 underline"
                >
                  Réinitialiser
                </button>
              )}
            </div>
          )}
        </div>

        {/* Filter by Email (text search - both personal and professional) */}
        <div className="border-b border-gray-200 px-4">
          <button
            onClick={() => toggleSection('emailSearch')}
            className="w-full flex items-center justify-between py-3 text-sm font-medium text-gray-700 hover:text-gray-900"
          >
            <span className="flex items-center gap-2">
              <Search size={16} />
              Recherche email
            </span>
            {openSections.emailSearch ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
          {openSections.emailSearch && (
            <div className="pb-3 space-y-3">
              <div>
                <label className="block text-xs text-gray-600 mb-1">Email professionnel</label>
                <input
                  type="text"
                  value={professionalEmailFilter}
                  onChange={(e) => onProfessionalEmailFilter(e.target.value)}
                  placeholder="Rechercher..."
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Email personnel</label>
                <input
                  type="text"
                  value={personalEmailFilter}
                  onChange={(e) => onPersonalEmailFilter(e.target.value)}
                  placeholder="Rechercher..."
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            </div>
          )}
        </div>

        {/* Filter by Current Salary */}
        <div className="border-b border-gray-200 px-4">
          <button
            onClick={() => toggleSection('currentSalary')}
            className="w-full flex items-center justify-between py-3 text-sm font-medium text-gray-700 hover:text-gray-900"
          >
            <span className="flex items-center gap-2">
              <DollarSign size={16} />
              Salaire actuel (k€)
            </span>
            {openSections.currentSalary ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
          {openSections.currentSalary && (
            <div className="pb-3">
              <div className="flex gap-2">
                <input
                  type="number"
                  value={salaryFilters.currentMin}
                  onChange={(e) => onSalaryFiltersChange({ ...salaryFilters, currentMin: e.target.value })}
                  placeholder="Min"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
                <input
                  type="number"
                  value={salaryFilters.currentMax}
                  onChange={(e) => onSalaryFiltersChange({ ...salaryFilters, currentMax: e.target.value })}
                  placeholder="Max"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            </div>
          )}
        </div>

        {/* Filter by Expected Salary */}
        <div className="border-b border-gray-200 px-4">
          <button
            onClick={() => toggleSection('expectedSalary')}
            className="w-full flex items-center justify-between py-3 text-sm font-medium text-gray-700 hover:text-gray-900"
          >
            <span className="flex items-center gap-2">
              <DollarSign size={16} />
              Salaire souhaité (k€)
            </span>
            {openSections.expectedSalary ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
          {openSections.expectedSalary && (
            <div className="pb-3">
              <div className="flex gap-2">
                <input
                  type="number"
                  value={salaryFilters.expectedMin}
                  onChange={(e) => onSalaryFiltersChange({ ...salaryFilters, expectedMin: e.target.value })}
                  placeholder="Min"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
                <input
                  type="number"
                  value={salaryFilters.expectedMax}
                  onChange={(e) => onSalaryFiltersChange({ ...salaryFilters, expectedMax: e.target.value })}
                  placeholder="Max"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            </div>
          )}
        </div>
      </div>

        {/* Clear all filters */}
        <div className="mt-4 pt-4 border-t border-gray-200 px-4">
          <button
            onClick={() => {
              onTagFilter([]);
              onPositionFilter([]);
              onBizdevProcessFilter([]);
              onOpenToJobOpportunitiesToggle(false);
              onPhoneFilter(false);
              onProfessionalEmailToggle(false);
              onPersonalEmailToggle(false);
              onCityFilter("");
              onPersonalEmailFilter("");
              onProfessionalEmailFilter("");
              onJobTitleFilter("");
              onSalaryFiltersChange({ currentMin: "", currentMax: "", expectedMin: "", expectedMax: "" });
            }}
            className="w-full text-sm text-indigo-600 hover:text-indigo-700 font-medium transition-colors text-center"
          >
            Effacer tous les filtres
          </button>
        </div>
      
    </>
  );

  // Retour conditionnel avec ou sans wrapper selon mode mobile
  if (isMobile) {
    return content;
  }

  return (
    <div className="w-64 bg-white border-t border-r border-gray-200 py-4 h-fit sticky top-10 z-30">
      <div className="flex items-center justify-between mb-2 px-4">
        <h3 className="text-lg font-semibold text-gray-900 m-0 flex items-center gap-2">
          <i className="bi bi-funnel text-lg"></i>
          Filtres
        </h3>
      </div>
      {content}
    </div>
  );
});

// Composant FilterBar simplifié - juste la recherche et les actions
export const FilterBar = React.memo(({
  selectedIndividuals,
  searchTerm,
  onSearch,
  onBulkActions,
}) => {
  return (
    <div className="relative sticky top-10 z-20 border-y  bg-white  p-3  mb-2">
      {/* Desktop: ligne unique */}
      <div className="hidden sm:flex items-center gap-4">
        {/* Section gauche - Barre de recherche */}
        <div className="flex-grow max-w-md">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <i className="bi bi-search text-gray-400 text-sm"></i>
            </div>
            <input
              type="text"
              placeholder="Rechercher par nom, entreprise ou email..."
              value={searchTerm}
              onChange={onSearch}
              className="w-full h-9 pl-10 pr-10 bg-white border border-gray-200 rounded-lg text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all"
            />
            {searchTerm && (
              <button
                onClick={() => onSearch({ target: { value: '' } })}
                className="absolute inset-y-0 right-0 pr-3 flex items-center transition-colors"
              >
                <i className="bi bi-x text-gray-400 hover:text-gray-600 text-sm"></i>
              </button>
            )}
          </div>
        </div>

        {/* Section droite - Actions sur la sélection */}
        {selectedIndividuals.length > 0 && (
          <div className="flex items-center gap-3 bg-indigo-50 border border-indigo-200 rounded-lg px-3 py-2">
            <span className="text-indigo-700 font-medium text-sm">
              {selectedIndividuals.length} sélectionné(s)
            </span>
            <button
              onClick={onBulkActions}
              className="flex items-center gap-2 px-3 py-1 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors text-sm font-medium"
              title="Actions multiples"
            >
              <i className="bi bi-lightning-fill"></i>
              Actions
            </button>
          </div>
        )}
      </div>

      {/* Mobile: deux lignes */}
      <div className="sm:hidden space-y-3">
        {/* Première ligne - Actions sur la sélection */}
        <div className="flex items-center justify-between gap-3">
          {selectedIndividuals.length > 0 && (
            <div className="flex items-center gap-2 bg-indigo-50 border border-indigo-200 rounded-lg px-2 py-1.5">
              <span className="text-indigo-700 font-medium text-xs">
                {selectedIndividuals.length} sélectionné(s)
              </span>
              <button
                onClick={onBulkActions}
                className="flex items-center gap-1 px-2 py-1 bg-indigo-600 hover:bg-indigo-700 text-white rounded text-xs font-medium"
                title="Actions multiples"
              >
                <i className="bi bi-lightning-fill text-xs"></i>
                Actions
              </button>
            </div>
          )}
        </div>

        {/* Deuxième ligne - Barre de recherche */}
        <div className="w-full">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <i className="bi bi-search text-gray-400 text-sm"></i>
            </div>
            <input
              type="text"
              placeholder="Rechercher par nom, entreprise ou email..."
              value={searchTerm}
              onChange={onSearch}
              className="w-full h-10 pl-10 pr-10 bg-white border border-gray-200 rounded-lg text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all"
            />
            {searchTerm && (
              <button
                onClick={() => onSearch({ target: { value: '' } })}
                className="absolute inset-y-0 right-0 pr-3 flex items-center transition-colors"
              >
                <i className="bi bi-x text-gray-400 hover:text-gray-600 text-sm"></i>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});